import type { SchemaNode } from './mcp-tool-json-schema'

interface ExampleRule {
  example: string
  formats?: string[]
  pattern?: RegExp
  /** Types the description match is trusted for; a format match always is. */
  types?: string[]
}

const RULES: ExampleRule[] = [
  { example: 'Sheet1!A1:B10', pattern: /\bA1 notation/i },
  { example: '+14155550100', pattern: /E\.?164/i },
  { example: 'name@example.com', formats: ['email'], pattern: /e-?mail address/i },
  {
    example: '2026-10-21T10:00:00Z',
    formats: ['date-time'],
    pattern: /ISO[ -]?8601|RFC ?3339|date-time|datetime/i,
  },
  { example: '2026-10-21', formats: ['date'], pattern: /YYYY-MM-DD/i },
  { example: '1792540800', pattern: /\bunix\b|\bepoch\b/i, types: ['integer', 'number'] },
  {
    example: 'https://example.com',
    formats: ['uri', 'url'],
    pattern: /\bURL\b|\bURI\b/i,
    types: ['string'],
  },
  {
    example: 'value1,value2',
    pattern: /comma[- ]separated|comma[- ]delimited/i,
    types: ['string'],
  },
]

const asExample = (value: unknown) =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : undefined

/** A value of the shape a text input expects: the schema's own example, else one its wording implies. */
export const textExample = (schema: SchemaNode, types: string[]): string | undefined => {
  const listed = Array.isArray(schema.examples) ? asExample(schema.examples[0]) : undefined
  const own = listed ?? asExample(schema.example)
  if (own !== undefined) return own
  // A declared format is more exact than wording, so every rule's format is tried first.
  const format = typeof schema.format === 'string' ? schema.format : undefined
  const byFormat = format && RULES.find((rule) => rule.formats?.includes(format))
  if (byFormat) return byFormat.example
  const description = typeof schema.description === 'string' ? schema.description : ''
  return RULES.find(
    (rule) =>
      rule.pattern?.test(description) &&
      (!rule.types || rule.types.some((type) => types.includes(type))),
  )?.example
}

const MAX_SUMMARY = 140
// "e.g." and "i.e." end no sentence.
const SENTENCE_END = /(?<!\b(?:e\.g|i\.e))[.?!](?=\s)/

export const firstSentence = (text: string | undefined) => {
  const flat = text?.replace(/\s+/g, ' ').trim()
  if (!flat) return undefined
  const end = flat.search(SENTENCE_END)
  const sentence = end === -1 ? flat : flat.slice(0, end + 1)
  if (sentence.length <= MAX_SUMMARY) return sentence
  const cut = sentence.slice(0, MAX_SUMMARY - 1)
  const space = cut.lastIndexOf(' ')
  return `${(space > MAX_SUMMARY / 2 ? cut.slice(0, space) : cut).trimEnd()}…`
}
